import { useState, useRef, useCallback, type PropsWithChildren, type FC, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import type { SwipeEventData } from 'react-swipeable'
import { type DeliveryVanHandle } from './DeliveryVan'
import { Swipeable } from './Swipeable'
import { dateToText } from './date-to-text'
import { minimalTime } from './minimal-time'
import { maxHeight, triggerHeight } from './constants'
import { PullContainer } from './PullContainer'
import { type Festive } from './festives'

type Props = PropsWithChildren<{
  children: ReactNode

  /**
   * @default 'default'
   */
  festive?: Festive

  onRefresh?: () => Promise<unknown>

  onRefreshComplete?: () => void

  /**
   * Here we tell you we are visually pulling or when its done (including the end of onRefresh and its animation)
   */
  onPulling?: (isPulling: boolean) => void
}>

/**
 * Only to be used for "Pull To Refresh" in our @dhl-ui/page package.
 * When you have inner containers using PTR please use our `PullToRefresh`
 * The delivery van drives during refresh and finishes braking before the indicator retracts.
 */
export const PagePullToRefresh: FC<Props> = ({
  children,
  festive = 'default',
  onRefresh,
  onRefreshComplete,
  onPulling,
}) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement | null>(null)
  const vanRef = useRef<DeliveryVanHandle | null>(null)
  const root = document.getElementById('root')

  const [translateY, setTranslateY] = useState(-1)
  const [isPulling, setIsPulling] = useState(false)
  const [lastUpdateText, setLastUpdatedText] = useState<string>(t('pullToRefresh.lastUpdated'))
  const [lastUpdated, setLastUpdated] = useState(new Date())
  const [offsetY, setOffsetY] = useState(0)
  const [isRetracting, setRetracting] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const setRefreshing = useCallback(
    (refresh: boolean) => {
      setIsRefreshing(refresh)
    },
    [setIsRefreshing]
  )

  const setUpdateText = (date: Date = lastUpdated): void => {
    setLastUpdatedText(
      t('pullToRefresh.lastUpdated', {
        date: dateToText(dayjs(date), t),
      })
    )
  }

  const onSwipeStart = (ev: SwipeEventData) => {
    const scrollTop = root?.scrollTop || document.documentElement.scrollTop || 0

    if (ev.dir === 'Down' && Math.abs(ev.deltaY) > Math.abs(ev.deltaX) && scrollTop < 50) {
      if (onRefresh) {
        onPulling?.(true)
      }

      setIsPulling(true)
      setUpdateText()
      setOffsetY(scrollTop)
    } else {
      setOffsetY(0)
    }
  }

  const onSwiping = (ev: SwipeEventData) => {
    if (!onRefresh) {
      return
    }

    if (ev.dir === 'Down' && isPulling) {
      let deltaY = Math.round(ev.deltaY - offsetY)
      if (deltaY < 0) {
        deltaY = 0
      }
      if (deltaY > maxHeight) {
        deltaY = maxHeight
      }

      if (translateY < triggerHeight && deltaY >= triggerHeight) {
        onRefreshComplete?.()
      }
      setTranslateY(deltaY)
    }
  }

  const retract = useCallback(() => {
    setIsPulling(false)
    setTranslateY(0)
    // we are in final stage and done with refreshing
    setRefreshing(false)
    setRetracting(true)
    setTimeout(() => {
      setTranslateY(-1)
      setRetracting(false)
      onPulling?.(false)
    }, 100)
  }, [setRefreshing, onPulling])

  const completeSwipe = async () => {
    if (translateY > 0) {
      setOffsetY(0)
      if (translateY >= triggerHeight) {
        setTranslateY(triggerHeight)
        if (onRefresh) {
          setRefreshing(true)
          try {
            vanRef.current?.start()
            setLastUpdatedText(t('pullToRefresh.updating'))
            await Promise.all([minimalTime(), onRefresh()])
          } catch {
            // do nothing
          } finally {
            if (ref.current) {
              setLastUpdated(new Date())
              setUpdateText(new Date())

              setTimeout(() => vanRef.current?.stop(), 600)
              setTimeout(() => retract(), 2400)
            }
          }
        }
      } else {
        retract()
      }
    }
  }

  return (
    <Swipeable
      delta={10}
      preventScrollOnSwipe={false}
      trackTouch
      trackMouse={false}
      onSwipeStart={onSwipeStart}
      onSwiping={onSwiping}
      onSwipedUp={completeSwipe}
      onSwipedDown={completeSwipe}
      onSwipedLeft={completeSwipe}
      onSwipedRight={completeSwipe}
      rotationAngle={0}
    >
      <PullContainer
        inputRef={ref}
        vanRef={vanRef}
        festive={festive}
        translateY={translateY}
        isRetracting={isRetracting}
        isRefreshing={isRefreshing}
        lastUpdateText={lastUpdateText}
      >
        {children}
      </PullContainer>
    </Swipeable>
  )
}
